// Fonte única dos modelos de declaração: o Gerador de declarações (Meus Documentos) e o
// "Usar modelo de declaração" do Elaborar Proposta leem daqui. O back valida os mesmos 8 ids e
// os ids de campo (ProposalDeclarationWriter); ele não guarda nem serve o texto dos modelos.

export type DeclarationTemplateId =
  | 'menor_aprendiz'
  | 'me_epp'
  | 'habilitacao'
  | 'fatos_impeditivos'
  | 'trabalho_forcado_degradante'
  | 'elaboracao_independente_proposta'
  | 'renuncia_vistoria'
  | 'carta_credenciamento';

export type DeclarationFieldId =
  | 'edital'
  | 'razao_social'
  | 'cnpj'
  | 'representante_nome'
  | 'representante_cargo'
  | 'representante_rg'
  | 'representante_rg_orgao'
  | 'representante_cpf'
  | 'cidade'
  | 'credenciado_nome'
  | 'credenciado_rg'
  | 'credenciado_rg_orgao'
  | 'credenciado_cpf';

export type DeclarationFieldValues = Partial<Record<DeclarationFieldId, string | null>>;

// `classe` é a classe que o Gerador sempre usou no span do valor.
export const DECLARATION_FIELDS: Record<DeclarationFieldId, { rotulo: string; classe: string }> = {
  edital: { rotulo: 'número do edital', classe: 'licitacao_edital' },
  razao_social: { rotulo: 'razão social', classe: 'razao_social' },
  cnpj: { rotulo: 'CNPJ', classe: 'cnpj' },
  representante_nome: { rotulo: 'nome do responsável', classe: 'responsavel' },
  representante_cargo: { rotulo: 'cargo do responsável', classe: 'cargo' },
  representante_rg: { rotulo: 'RG do responsável', classe: 'numero_rg' },
  representante_rg_orgao: { rotulo: 'órgão expedidor do RG', classe: 'orgao_expedidor' },
  representante_cpf: { rotulo: 'CPF do responsável', classe: 'numero_cpf' },
  cidade: { rotulo: 'cidade', classe: 'cidade' },
  credenciado_nome: { rotulo: 'nome do credenciado', classe: 'credenciado' },
  credenciado_rg: { rotulo: 'RG do credenciado', classe: 'numero_rg_credenciado' },
  credenciado_rg_orgao: {
    rotulo: 'órgão expedidor do RG do credenciado',
    classe: 'orgao_expedidor_credenciado',
  },
  credenciado_cpf: { rotulo: 'CPF do credenciado', classe: 'numero_cpf_credenciado' },
};

export interface DeclarationTemplate {
  id: DeclarationTemplateId;
  label: string;
  titulo: string;
  // Parágrafo justificado com a qualificação e o que se declara; `{campo}` marca cada valor.
  qualificacao: string;
  // Só no Menor/Aprendiz: no Gerador sai em vermelho; no HTML da proposta, parágrafo normal.
  ressalva?: string;
  // Só na Elaboração Independente: a lista I a VI.
  itens?: string[];
}

const QUALIFICACAO =
  '{razao_social} inscrita no CNPJ sob nº {cnpj} por intermédio de seu representante legal, ' +
  'Sr.(a) {representante_nome}, portador(a) da Carteira de Identidade nº {representante_rg} ' +
  'Órgão Expedidor {representante_rg_orgao} e do C.P.F nº {representante_cpf}, ';

export const DECLARATION_TEMPLATES: DeclarationTemplate[] = [
  {
    id: 'menor_aprendiz',
    label: 'Declaração Menor/Aprendiz',
    titulo: 'DECLARAÇÃO QUE NÃO EMPREGA MENOR DE IDADE, SALVO NA CONDIÇÃO DE APRENDIZ',
    qualificacao: `${QUALIFICACAO}DECLARA, para fins de cumprimento do disposto no inciso XXXIII do Art. 7º da Constituição Federal, que não emprega menor de dezoito anos em trabalho noturno, perigoso ou insalubre e que não emprega menor de dezesseis anos.`,
    ressalva: 'Ressalva: emprega menor, a partir de quatorze anos, na condição de aprendiz.',
  },
  {
    id: 'me_epp',
    label: 'Declaração ME/EPP',
    titulo: 'DECLARAÇÃO DE MICRO EMPRESA OU EMPRESA DE PEQUENO PORTE',
    qualificacao: `${QUALIFICACAO}DECLARA, sob a as penas da lei que não ultrapassou o limite de faturamento e cumpre os requisitos estabelecidos no art. 3º da Lei Complementar nº 123, de 14 de dezembro de 2006, sendo apta a usufruir o tratamento favorecido estabelecido nos arts. 42º ao 49º da referida Lei Complementar.`,
  },
  {
    id: 'habilitacao',
    label: 'Declaração de Habilitação',
    titulo: 'DECLARAÇÃO DE HABILITAÇÃO',
    qualificacao: `${QUALIFICACAO}DECLARA, que está ciente e concorda com as condições contidas no edital e seus anexos, bem como de que cumpre plenamente os requisitos de habilitação definidos no edital em epígrafe.`,
  },
  {
    id: 'fatos_impeditivos',
    label: 'Declaração de Inexistência de Fatos Impeditivos',
    titulo: 'DECLARAÇÃO DE INEXISTÊNCIA DE FATOS IMPEDITIVOS',
    qualificacao:
      '{razao_social} inscrita no CNPJ sob nº {cnpj} por intermédio de seu representante legal, ' +
      'Sr.(a) {representante_nome}, portador(a) da Carteira de Identidade nº {representante_rg}, ' +
      'Órgão Expedidor {representante_rg_orgao} e do CPF nº {representante_cpf}, DECLARA, sob as ' +
      'penas da lei, que até a presente data inexistem fatos impeditivos para a habilitação no ' +
      'presente processo licitatório, ciente da obrigatoriedade de declarar ocorrências posteriores.',
  },
  {
    id: 'trabalho_forcado_degradante',
    label: 'Declaração sobre Trabalho Forçado e/ou Degradante',
    titulo: 'DECLARAÇÃO DE NÃO TRABALHO FORÇADO E DEGRADANTE',
    qualificacao: `${QUALIFICACAO}DECLARA, que não possui, em sua cadeia produtiva, empregados executando trabalho degradante ou forçado, observando o disposto nos incisos III e IV do art. 1º e no inc. III do art. 5º da Constituição Federal.`,
  },
  {
    id: 'elaboracao_independente_proposta',
    label: 'Declaração de Elaboração independente de Proposta',
    titulo: 'DECLARAÇÃO DE ELABORAÇÃO INDEPENDENTE DE PROPOSTA',
    qualificacao: `${QUALIFICACAO}DECLARA, sob as penas da lei, em especial o art. 299 do Código Penal Brasileiro que:`,
    itens: [
      'a proposta apresentada para participar da licitação em epígrafe foi elaborada de maneira independente pelo Licitante, e o conteúdo da proposta não foi, no todo ou em parte, direta ou indiretamente, informado, discutido ou recebido de qualquer outro participante potencial ou de fato da licitação em epígrafe, por qualquer meio ou por qualquer pessoa;',
      'a intenção de apresentar a proposta elaborada para participar da licitação em epígrafe não foi informada, discutida ou recebida de qualquer outro participante potencial ou de fato da licitação em epígrafe, por qualquer meio ou por qualquer pessoa;',
      'que não tentou, por qualquer meio ou por qualquer pessoa, influir na decisão de qualquer outro participante potencial ou de fato da licitação em epígrafe quanto a participar ou não da referida licitação;',
      'que o conteúdo da proposta apresentada para participar da licitação em epígrafe não será, no todo ou em parte, direta ou indiretamente, comunicado ou discutido com qualquer outro participante potencial ou de fato da licitação em epígrafe antes da adjudicação do objeto da referida licitação;',
      'que o conteúdo da proposta apresentada para participar da licitação em epígrafe não foi, no todo ou em parte, direta ou indiretamente, informado, discutido ou recebido de qualquer integrante deste órgão antes da abertura oficial das propostas; e',
      'que está plenamente ciente do teor e da extensão desta declaração e que detém plenos poderes e informações para firmá-la.',
    ],
  },
  {
    id: 'renuncia_vistoria',
    label: 'Declaração de Renúncia de Vistoria',
    titulo: 'DECLARAÇÃO DE RENÚNCIA DE VISTORIA',
    qualificacao: `${QUALIFICACAO}DECLARA, sob as penas da lei que, optamos pela não realização de vistoria assumindo inteiramente a responsabilidade ou consequências por essa omissão, mantendo as garantias que vincularem nossa proposta ao presente processo licitatório.`,
  },
  {
    id: 'carta_credenciamento',
    label: 'Carta de Credenciamento',
    titulo: 'CARTA DE CREDENCIAMENTO',
    qualificacao:
      '{razao_social} inscrita no CNPJ sob nº {cnpj} por intermédio de seu representante legal, ' +
      'Sr.(a) {representante_nome}, portador(a) da Carteira de Identidade nº {representante_rg}, ' +
      'Órgão Expedidor {representante_rg_orgao}, e do C.P.F nº {representante_cpf}, pela presente ' +
      'CREDENCIA o Sr. {credenciado_nome}, portador da carteira de Identidade nº {credenciado_rg}, ' +
      'Órgão Expedidor {credenciado_rg_orgao}, e CPF nº {credenciado_cpf}, para representá-la na ' +
      'Licitação em epígrafe supra mencionada, outorgando-lhe poderes para concordar, desistir, ' +
      'renunciar, transigir, firmar recibos, assinar Atas e outros documentos, acompanhar todo o ' +
      'processo Licitatório até o seu final, tomar ciência de outras propostas, podendo para ' +
      'tanto, praticar todos os atos necessários para o bom e fiel cumprimento deste mandato.',
  },
];

export const declarationTemplateById = (id: string | null | undefined) =>
  DECLARATION_TEMPLATES.find((template) => template.id === id) ?? null;

export type TemplateSegment = { texto: string } | { campo: DeclarationFieldId };

// Quebra o texto nos marcadores `{campo}`.
export const templateSegments = (texto: string): TemplateSegment[] =>
  texto
    .split(/(\{[a-z_]+\})/)
    .filter((parte) => parte !== '')
    .map((parte) => {
      const match = parte.match(/^\{([a-z_]+)\}$/);
      return match ? { campo: match[1] as DeclarationFieldId } : { texto: parte };
    });

// Campos na ordem em que aparecem, sem repetir (o responsável aparece na qualificação e na
// assinatura).
export const templateFieldIds = (template: DeclarationTemplate): DeclarationFieldId[] => {
  const ids: DeclarationFieldId[] = ['edital'];
  templateSegments(template.qualificacao).forEach((segmento) => {
    if ('campo' in segmento && !ids.includes(segmento.campo)) ids.push(segmento.campo);
  });
  (['cidade', 'representante_nome', 'representante_cargo'] as DeclarationFieldId[]).forEach(
    (id) => {
      if (!ids.includes(id)) ids.push(id);
    },
  );
  return ids;
};

const escape = (texto: string) =>
  texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const campoHtml = (id: DeclarationFieldId, valores: DeclarationFieldValues) => {
  const valor = (valores[id] ?? '').trim();
  const texto = valor || `[Inserir ${DECLARATION_FIELDS[id].rotulo}]`;
  return `<strong><span data-campo="${id}">${escape(texto)}</span></strong>`;
};

const textoHtml = (texto: string, valores: DeclarationFieldValues) =>
  templateSegments(texto)
    .map((segmento) =>
      'campo' in segmento ? campoHtml(segmento.campo, valores) : escape(segmento.texto),
    )
    .join('');

// HTML restrito para o editor do Elaborar Proposta: só p, ol, li, strong e span[data-campo], com
// text-align. `data` já vem escrita (o mesmo formato do Gerador, moment().format('LL')).
export const renderDeclarationTemplateHtml = (
  id: DeclarationTemplateId,
  valores: DeclarationFieldValues,
  data: string,
): string => {
  const template = declarationTemplateById(id);
  if (!template) return '';
  const partes = [
    `<p style="text-align: center"><strong>${escape(template.titulo)}</strong></p>`,
    `<p>Ref.: ${campoHtml('edital', valores)}</p>`,
    `<p style="text-align: justify">${textoHtml(template.qualificacao, valores)}</p>`,
  ];
  if (template.ressalva) partes.push(`<p>${escape(template.ressalva)}</p>`);
  if (template.itens) {
    partes.push(
      `<ol>${template.itens.map((item) => `<li><p>${escape(item)}</p></li>`).join('')}</ol>`,
    );
  }
  partes.push(
    `<p>${campoHtml('cidade', valores)}, ${escape(data)}</p>`,
    '<p>_______________________________________</p>',
    `<p>${campoHtml('representante_nome', valores)} - ${campoHtml(
      'representante_cargo',
      valores,
    )}</p>`,
  );
  return partes.join('');
};
